import { useState, useEffect, forwardRef } from 'react'
import { Moon, Sun, Clock, EyeOff, Eye, Thermometer } from 'lucide-react'
import { format } from 'date-fns'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { cn } from '@/lib/utils'
function ToggleMenuItem({ isOn, onToggle, labelOn, labelOff, contextLabel }) {
  return (
    <ContextMenuItem onClick={onToggle}>
      {isOn ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
      {isOn
        ? contextLabel
          ? `Hide ${contextLabel}`
          : labelOn
        : contextLabel
          ? `Show ${contextLabel}`
          : labelOff}
    </ContextMenuItem>
  )
}
export function HeaderDisplay({
  weatherUnit,
  timeFormat,
  showTime,
  showWeather,
  onToggleTimeVisibility,
  onToggleWeatherVisibility,
  onToggleTimeFormat,
  onToggleWeatherUnit,
}) {
  const [time, setTime] = useState(new Date())
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])
  const hours = time.getHours()
  const isNight = hours >= 18 || hours < 6
  return (
    <div className="flex items-center gap-1.5">
      <WeatherIconMenu
        isNight={isNight}
        showWeather={showWeather}
        onToggleWeatherVisibility={onToggleWeatherVisibility}
      />
      <TimeDisplayMenu
        showTime={showTime}
        time={time}
        timeFormat={timeFormat}
        onToggleTimeFormat={onToggleTimeFormat}
        onToggleTimeVisibility={onToggleTimeVisibility}
      />
      <WeatherUnitMenu
        showWeather={showWeather}
        weatherUnit={weatherUnit}
        onToggleWeatherUnit={onToggleWeatherUnit}
        onToggleWeatherVisibility={onToggleWeatherVisibility}
      />
    </div>
  )
}
const WeatherIconMenu = ({ isNight, showWeather, onToggleWeatherVisibility }) => (
  <ContextMenu>
    <ContextMenuTrigger asChild>
      <div className={cn('transition-opacity duration-300', !showWeather && 'opacity-0')}>
        {isNight ? (
          <Moon className="h-4 w-4 text-foreground/70" />
        ) : (
          <Sun className="h-4 w-4 text-primary/70" />
        )}
      </div>
    </ContextMenuTrigger>
    <ContextMenuContent>
      <ToggleMenuItem
        isOn={showWeather}
        onToggle={onToggleWeatherVisibility}
        contextLabel="Weather"
      />
    </ContextMenuContent>
  </ContextMenu>
)
const TimeDisplayMenu = ({
  showTime,
  time,
  timeFormat,
  onToggleTimeFormat,
  onToggleTimeVisibility,
}) => (
  <ContextMenu>
    <ContextMenuTrigger asChild>
      <TimeDisplay showTime={showTime} time={time} timeFormat={timeFormat} />
    </ContextMenuTrigger>
    <ContextMenuContent>
      {showTime && (
        <ContextMenuItem onClick={onToggleTimeFormat}>
          <Clock className="mr-2 h-4 w-4" />
          {timeFormat === '12' ? 'Use 24h' : 'Use 12h'}
        </ContextMenuItem>
      )}
      <ToggleMenuItem isOn={showTime} onToggle={onToggleTimeVisibility} contextLabel="Time" />
    </ContextMenuContent>
  </ContextMenu>
)
const TimeDisplay = forwardRef(({ showTime, time, timeFormat, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('transition-opacity duration-300', !showTime && 'opacity-0')}
    {...props}
  >
    <h1 className="text-base font-bold tracking-tight tabular-nums cursor-default select-none text-foreground/80">
      {timeFormat === '12' ? (
        <>
          {format(time, 'h:mm')}
          <span className="text-[11px] ml-1 font-semibold text-muted-foreground/40">
            {format(time, 'a')}
          </span>
        </>
      ) : (
        format(time, 'H:mm')
      )}
    </h1>
  </div>
))
TimeDisplay.displayName = 'TimeDisplay'
const WeatherUnitMenu = ({
  showWeather,
  weatherUnit,
  onToggleWeatherUnit,
  onToggleWeatherVisibility,
}) => (
  <ContextMenu>
    <ContextMenuTrigger asChild>
      <div
        className={cn(
          'text-sm font-semibold ml-1 text-muted-foreground/40 flex items-center gap-0.5 cursor-default select-none transition-opacity duration-300',
          !showWeather && 'opacity-0',
        )}
      >
        {weatherUnit === 'C' ? '24' : '75'}{' '}
        <span className="text-xs align-top">°{weatherUnit}</span>
      </div>
    </ContextMenuTrigger>
    <ContextMenuContent>
      {showWeather && (
        <ContextMenuItem onClick={onToggleWeatherUnit}>
          <Thermometer className="mr-2 h-4 w-4" />
          {weatherUnit === 'C' ? 'Fahrenheit' : 'Celsius'}
        </ContextMenuItem>
      )}
      <ToggleMenuItem
        isOn={showWeather}
        onToggle={onToggleWeatherVisibility}
        contextLabel="Weather"
      />
    </ContextMenuContent>
  </ContextMenu>
)
